import { MOBILE_APP_SDK_URL } from './shared-mobile-app.consts';
import type {
  MobileAppClient,
  MobileAppEmbedConfig,
  MobileAppQueueState,
  MobileAppSession,
} from './shared-mobile-app.types';

declare global {
  interface Window {
    appetize?: {
      getClient: (
        target: string | HTMLElement,
        config?: MobileAppEmbedConfig
      ) => Promise<MobileAppClient>;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

export function loadMobileAppSdk(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new Error('Mobile App SDK can only be loaded in the browser')
    );
  }

  if (window.appetize?.getClient) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${MOBILE_APP_SDK_URL}"]`
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => {
          scriptPromise = null;
          existing.remove();
          reject(new Error('Existing Mobile App SDK script failed to load'));
        },
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.src = MOBILE_APP_SDK_URL;
    script.async = true;
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener(
      'error',
      () => {
        scriptPromise = null;
        script.remove();
        reject(new Error('Failed to load Mobile App SDK'));
      },
      { once: true }
    );
    document.head.appendChild(script);
  });

  return scriptPromise;
}

const MOBILE_APP_EMBED_DISPLAY_PARAMS: Record<string, string> = {
  scale: 'auto',
  deviceColor: 'black',
  centered: 'both',
  autoplay: 'true',
};

export function buildMobileAppEmbedUrl(
  embedUrl: string,
  language?: string | null
): string {
  try {
    const url = new URL(embedUrl);
    for (const [key, value] of Object.entries(
      MOBILE_APP_EMBED_DISPLAY_PARAMS
    )) {
      url.searchParams.set(key, value);
    }
    if (language) {
      url.searchParams.set('language', language);
    }
    return url.toString();
  } catch {
    return embedUrl;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object';
}

export function parseMobileAppQueuePayload(
  payload: unknown
): MobileAppQueueState | null {
  if (!isRecord(payload)) return null;
  const type = typeof payload.type === 'string' ? payload.type : 'unknown';
  const position = typeof payload.position === 'number' ? payload.position : 0;
  const name = typeof payload.name === 'string' ? payload.name : undefined;
  return { type, position, name };
}

export function readMobileAppErrorMessage(payload: unknown): string {
  if (payload instanceof Error) return payload.message;
  if (isRecord(payload) && typeof payload.message === 'string') {
    return payload.message;
  }
  return 'Unknown error';
}

export function isMobileAppSession(value: unknown): value is MobileAppSession {
  if (!isRecord(value)) return false;
  return typeof value.on === 'function' && typeof value.end === 'function';
}
